<template>
  <div class="editor-wrapper d-flex" :class="{ ...inputClasses }">
    <div class="editor-tooltip"></div>
    <Editor
      ref="editor"
      v-model="inputValue"
      class="editor inline"
      :init="{
        images_upload_handler: imageUploadCallback,
        placeholder,
        language,
        menubar,
        plugins,
        height,
        max_height: maxHeight,
        min_height: minHeight,
        toolbar_mode: toolbarMode,
        statusbar,
        contextmenu,
        file_picker_callback: fetchImage,
        external_plugins: {
          formula: '/tinymce/plugins/tinymce-formula/plugin.js',
        },
        fixed_toolbar_container: fixedToolbarContainer,
      }"
      :disabled="disabled"
      inline
      :toolbar="toolbarSettings"
      @focus="focusHandler"
      @blur="blurHandler"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRefs } from "vue";
import Editor from "@tinymce/tinymce-vue";
const emit = defineEmits([
  "update:modelValue",
  "input",
  "focusEvent",
  "blurEvent",
]);
const props = defineProps({
  language: {
    type: String,
    default: "ru",
  },
  imageUploadCallback: {
    type: Function,
    default: () => {},
  },
  toolbarSettings: {
    type: String,
    default: "italic bold underline charmap",
  },
  menubar: {
    type: Boolean,
    default: false,
  },
  statusbar: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  plugins: {
    type: String,
    default: "",
  },
  fixedToolbarContainer: {
    type: String,
    default: "",
  },
  contextmenu: {
    type: String,
    default: "",
  },
  toolbarMode: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  height: {
    type: Number,
    default: 350,
  },
  maxHeight: {
    type: Number,
    default: Math.max(window.innerHeight - 100, 300),
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
  minHeight: {
    type: Number,
    default: 350,
  },
  modelValue: {
    type: String,
    default: "",
  },
});
const {
  language,
  toolbarSettings,
  menubar,
  statusbar,
  disabled,
  plugins,
  fixedToolbarContainer,
  contextmenu,
  toolbarMode,
  placeholder,
  hasErrors,
  height,
  imageUploadCallback,
  maxHeight,
  minHeight,
  modelValue,
} = toRefs(props);
const editor = ref<any>(null);
const isFocused = ref<boolean>(false);
const inputValue = computed({
  get() {
    return modelValue.value;
  },
  set(val) {
    emit("update:modelValue", val);
  },
});
const inputClasses = computed(() => ({
  "editor-wrapper--status-focused": isFocused.value,
  "editor-wrapper--status-disabled": disabled.value,
  "editor-wrapper--has-value": inputValue.value,
  "editor-wrapper--has-errors": hasErrors.value,
}));
function focusHandler() {
  isFocused.value = true;
  emit("focusEvent");
}
function blurHandler() {
  isFocused.value = false;
  emit("blurEvent");
}
function fetchImage(cb: Function) {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");

  /*
      Note: In modern browsers input[type="file"] is functional without
      even adding it to the DOM, but that might not be the case in some older
      or quirky browsers like IE, so you might want to add it to the DOM
      just in case, and visually hide it. And do not forget do remove it
      once you do not need it anymore.
    */
  const blobCache = editor.value.getEditor().editorUpload.blobCache;
  input.onchange = function (e: Event) {
    const { files } = e.target as HTMLInputElement;
    if (files === null) return;
    const file = files[0];
    const reader: any = new FileReader();
    // const reader: FileReader = new FileReader();
    // const readerResult: string | ArrayBuffer | null = reader.result
    reader.onload = function () {
      /*
        Note: Now we need to register the blob in TinyMCEs image blob
        registry. In the next release this part hopefully won't be
        necessary, as we are looking to handle it internally.
      */

      const id = "blobid" + new Date().getTime();
      // if (readerResult === null) return;
      const base64 = reader.result.split(",")[1];
      const blobInfo = blobCache.create(id, file, base64);
      blobCache.add(blobInfo);

      /* call the callback and populate the Title field with the file name */
      cb(blobInfo.blobUri(), { title: file.name });
    };
    reader.readAsDataURL(file);
  };

  input.click();
}
</script>

<style lang="scss" scoped>
.input__actions {
  background-color: #fff;
  display: flex;
  align-items: center;
  padding-right: 0.5rem;
  position: absolute;
  top: 6px;
  right: 10px;
}

.editor-tooltip {
  position: absolute;
  top: -53px;
  left: -3px;
  width: calc(100% + 6px);
  background-color: #fff;
}

.editor-wrapper {
  position: relative;
  border: 1px solid var(--stroke-select);
  border-radius: var(--border-radius);
  min-height: 52px;
  height: auto;

  &:hover {
    border: 1px solid var(--color-icon-primary);
    outline: var(--border-hover-color) solid 1px;
  }
  &--status-focused {
    border-color: var(--border-focus-color);
    outline: var(--border-focus-color) solid 1px;
    &:hover {
      border-color: var(--border-focus-color);
      outline: var(--border-focus-color) solid 1px;
    }
  }
  .editor.inline {
    font-size: 14px;
    line-height: 20px;
    outline: none;
    width: 100%;
    color: var(--color-text-primary);
    border-radius: var(--border-radius);
    padding: 16px 48px 16px 16px;
    background-color: var(--color-bg-primary);
    transition: 0.2s;
    &.mce-content-body:not([dir="rtl"])[data-mce-placeholder]:not(
        .mce-visualblocks
      )::before {
      left: 16px;
      pointer-events: none;
    }
    :deep(p) {
      margin: 0 !important;
    }
  }
  &--has-errors {
    border: 1px solid var(--error-color);
    &:hover {
      outline: none;
    }
    &.editor-wrapper--status-focused {
      border-color: rgba(var(--color-icon-error), 0.25);
      outline: rgba(var(--color-icon-error), 0.25) solid 1px;
      &:hover {
        outline: rgba(var(--color-icon-error), 0.25) solid 1px;
      }
    }
  }
}
</style>
